import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Chip,
  Paper,
  IconButton,
} from '@mui/material';
import { FiRefreshCw, FiDownload, FiCheckCircle, FiAlertCircle } from 'react-icons/fi';
import { fileAPI } from '../../api/apiHelper';

function parseInsightsFromLlmResponse(raw) {
  if (!raw) return null;
  try {
    // Strip markdown code fences if present
    const cleaned = raw.replace(/^```json\s*/i, '').replace(/^```\s*/i, '').replace(/```\s*$/i, '').trim();
    return JSON.parse(cleaned);
  } catch (e) {
    return { rawText: raw };
  }
}

export default function PreCallInsights() {
  const [docData, setDocData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [parsedInsights, setParsedInsights] = useState(null);

  const organizationId = localStorage.getItem('orgId') || '';

  const fetchPreCallInsights = useCallback(async () => {
    setLoading(true);
    setError(null);

    if (!organizationId) {
      setError('Organization ID not found. Please log in again.');
      setLoading(false);
      return;
    }

    try {
      // apiRequest strips the outer { success, data } wrapper and returns the inner data
      // So response here is the document object: { _id, llmResponse, llmStatus, metadata, ... }
      const response = await fileAPI.getPreCallInsights(organizationId);
      const doc = response?.data || response;

      if (!doc || !doc.llmResponse) {
        setError('No pre-call insights available yet. Please upload company and competitor documents first.');
        setLoading(false);
        return;
      }

      setDocData(doc);
      setParsedInsights(parseInsightsFromLlmResponse(doc.llmResponse));
    } catch (err) {
      console.error('Error fetching pre-call insights:', err);
      setError(err.message || 'Failed to load pre-call insights');
    } finally {
      setLoading(false);
    }
  }, [organizationId]);

  useEffect(() => {
    fetchPreCallInsights();
  }, [fetchPreCallInsights]);

  const downloadInsights = () => {
    const text = docData?.llmResponse || '';
    const blob = new Blob([text], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `pre-call-insights-${new Date().toISOString().split('T')[0]}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="warning">
          <Typography variant="h6" gutterBottom>No Pre-Call Insights Available</Typography>
          <Typography variant="body2" mb={2}>{error}</Typography>
          <Typography variant="body2">
            Please upload company documents and competitor documents first, then wait a few minutes for processing.
          </Typography>
        </Alert>
      </Box>
    );
  }

  return (
    <Box p={3}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" fontWeight={700} gutterBottom>
            🎯 Pre-Call Sales Insights
          </Typography>
          <Typography variant="body2" color="text.secondary">
            AI-powered guidance based on your company and competitor documents
          </Typography>
        </Box>
        <Box display="flex" gap={1}>
          <IconButton onClick={fetchPreCallInsights} color="primary" title="Refresh">
            <FiRefreshCw />
          </IconButton>
          <IconButton onClick={downloadInsights} color="primary" title="Download">
            <FiDownload />
          </IconButton>
        </Box>
      </Box>

      {/* Metadata chips */}
      {docData && (
        <Box mb={3} display="flex" gap={2} flexWrap="wrap">
          {docData.processingCompletedAt && (
            <Chip
              label={`Generated: ${new Date(docData.processingCompletedAt).toLocaleString()}`}
              color="primary"
              variant="outlined"
            />
          )}
          {docData.metadata?.companyDocsCount != null && (
            <Chip
              label={`Company Docs: ${docData.metadata.companyDocsCount}`}
              color="success"
              variant="outlined"
            />
          )}
          {docData.metadata?.competitorDocsCount != null && (
            <Chip
              label={`Competitor Docs: ${docData.metadata.competitorDocsCount}`}
              color="warning"
              variant="outlined"
            />
          )}
          <Chip
            label={`Status: ${docData.llmStatus || 'unknown'}`}
            color={docData.llmStatus === 'completed' ? 'success' : 'default'}
            variant="outlined"
          />
        </Box>
      )}

      {/* Insights Display */}
      {parsedInsights && (
        <Box>
          {parsedInsights.rawText ? (
            <Paper elevation={2} sx={{ p: 3, bgcolor: '#f9f9f9' }}>
              <Typography variant="body1" whiteSpace="pre-wrap">
                {parsedInsights.rawText}
              </Typography>
            </Paper>
          ) : (
            <>
              {/* Talking Points */}
              {parsedInsights.talkingPoints?.length > 0 && (
                <Card sx={{ mb: 3 }}>
                  <CardContent>
                    <Typography variant="h6" fontWeight={700} gutterBottom color="primary">
                      💬 Key Talking Points
                    </Typography>
                    <Box component="ul" pl={2}>
                      {parsedInsights.talkingPoints.map((point, idx) => (
                        <Typography component="li" key={idx} variant="body1" mb={1}>
                          {point}
                        </Typography>
                      ))}
                    </Box>
                  </CardContent>
                </Card>
              )}

              {/* Competitor Weaknesses */}
              {parsedInsights.competitorWeaknesses?.length > 0 && (
                <Card sx={{ mb: 3 }}>
                  <CardContent>
                    <Typography variant="h6" fontWeight={700} gutterBottom color="error">
                      🥊 Competitor Weaknesses
                    </Typography>
                    <Box component="ul" pl={2}>
                      {parsedInsights.competitorWeaknesses.map((item, idx) => (
                        <Typography component="li" key={idx} variant="body1" mb={1}>
                          {typeof item === 'string' ? item : (item.weakness || item.competitor || JSON.stringify(item))}
                        </Typography>
                      ))}
                    </Box>
                  </CardContent>
                </Card>
              )}

              {/* Things To Do */}
              {parsedInsights.thingsToDo?.length > 0 && (
                <Card sx={{ mb: 3 }}>
                  <CardContent>
                    <Typography variant="h6" fontWeight={700} gutterBottom color="success.main">
                      ✅ Things to DO
                    </Typography>
                    <Box component="ul" pl={2}>
                      {parsedInsights.thingsToDo.map((item, idx) => (
                        <Typography component="li" key={idx} variant="body1" mb={1} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                          <FiCheckCircle style={{ color: 'green', marginTop: 3, flexShrink: 0 }} />
                          {item}
                        </Typography>
                      ))}
                    </Box>
                  </CardContent>
                </Card>
              )}

              {/* Things To Avoid */}
              {parsedInsights.thingsToAvoid?.length > 0 && (
                <Card sx={{ mb: 3 }}>
                  <CardContent>
                    <Typography variant="h6" fontWeight={700} gutterBottom color="error">
                      ❌ Things to AVOID
                    </Typography>
                    <Box component="ul" pl={2}>
                      {parsedInsights.thingsToAvoid.map((item, idx) => (
                        <Typography component="li" key={idx} variant="body1" mb={1} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                          <FiAlertCircle style={{ color: 'red', marginTop: 3, flexShrink: 0 }} />
                          {item}
                        </Typography>
                      ))}
                    </Box>
                  </CardContent>
                </Card>
              )}

              {/* Key Features */}
              {parsedInsights.keyFeatures?.length > 0 && (
                <Card sx={{ mb: 3 }}>
                  <CardContent>
                    <Typography variant="h6" fontWeight={700} gutterBottom color="primary">
                      ⭐ Key Features to Highlight
                    </Typography>
                    <Box component="ul" pl={2}>
                      {parsedInsights.keyFeatures.map((feature, idx) => (
                        <Typography component="li" key={idx} variant="body1" mb={1}>
                          {feature}
                        </Typography>
                      ))}
                    </Box>
                  </CardContent>
                </Card>
              )}
            </>
          )}
        </Box>
      )}
    </Box>
  );
}
